import { useState, useCallback } from "react";
import { reviewStore, UserReview, ReviewStatus } from "@/utils/reviewStore";

interface StaticReview {
  id: string;
  name: string;
  location: string;
  rating: number;
  date: string;
  text: string;
  accommodation: string;
  status: ReviewStatus;
  reply?: string;
  isStatic: true;
}

const STATIC_REVIEWS: StaticReview[] = [
  { id: "s1", name: "김OO", location: "서울 강남", rating: 5, date: "2025년 3월", text: "부모님 모시고 갔는데 일본어 하나도 못해도 너무 편했어요. 호스트분이 카카오톡으로 맛집도 추천해주시고 응급상황에서 병원까지 연결해 주셨어요.", accommodation: "하카타 럭셔리 패밀리 아파트", status: "approved", reply: "소중한 리뷰 감사드립니다! 다음 방문에도 최선을 다하겠습니다 :)", isStatic: true },
  { id: "s2", name: "박OO", location: "부산 해운대", rating: 5, date: "2025년 1월", text: "솔직히 일본 여행 처음이라 무서웠는데, 한국어로 체크인부터 퇴실까지 모두 안내해 주셔서 너무 든든했어요.", accommodation: "나카스 리버뷰 로맨틱 룸", status: "approved", isStatic: true },
  { id: "s3", name: "이OO", location: "대구", rating: 5, date: "2024년 12월", text: "호스트 언니가 한국 분이라 정말 편했고, 텐진 주변 쇼핑 정보부터 면세 꿀팁까지 다 알려주셨어요.", accommodation: "텐진 감성 한옥 스튜디오", status: "approved", isStatic: true },
  { id: "s4", name: "최OO", location: "인천", rating: 5, date: "2025년 2월", text: "5살, 8살 아이 데리고 갔는데 호스트분이 유모차도 빌려주시고 아이 친화 식당 리스트도 만들어주셨어요.", accommodation: "오호리 공원 뷰 모던 하우스", status: "approved", isStatic: true },
  { id: "s5", name: "정OO", location: "광주", rating: 5, date: "2025년 3월", text: "야간에 갑자기 약이 필요했는데, 밤 11시에 카카오톡 연락드렸더니 바로 응답 주시고 근처 약국 위치와 일본어 메모까지 보내주셨어요.", accommodation: "야쿠인 카페 거리 빈티지 룸", status: "approved", isStatic: true },
  { id: "s6", name: "한OO", location: "제주", rating: 3, date: "2025년 4월", text: "전반적으로 좋았지만 Wi-Fi 속도가 조금 느렸고, 수건이 부족했어요. 호스트 응답은 빠른 편이었습니다.", accommodation: "하카타 비즈니스 클린 원룸", status: "pending", isStatic: true },
];

type AnyReview = StaticReview | (UserReview & { isStatic: false });

const statusLabel: Record<ReviewStatus, { label: string; className: string }> = {
  pending: { label: "검토 대기", className: "bg-amber-100 text-amber-700" },
  approved: { label: "게시 중", className: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "숨김", className: "bg-stone-100 text-stone-500" },
};

export default function ReviewsTab() {
  const [filter, setFilter] = useState<"all" | ReviewStatus>("all");
  const [replyTarget, setReplyTarget] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [userReviews, setUserReviews] = useState<UserReview[]>(() => reviewStore.getAll());
  const [staticStatuses, setStaticStatuses] = useState<Record<string, ReviewStatus>>(
    () => Object.fromEntries(STATIC_REVIEWS.map((r) => [r.id, r.status]))
  );
  const [staticReplies, setStaticReplies] = useState<Record<string, string>>(
    () => Object.fromEntries(STATIC_REVIEWS.filter((r) => r.reply).map((r) => [r.id, r.reply!]))
  );

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const refreshUserReviews = useCallback(() => {
    setUserReviews(reviewStore.getAll());
  }, []);

  const allReviews: AnyReview[] = [
    ...STATIC_REVIEWS.map((r) => ({
      ...r,
      status: staticStatuses[r.id] ?? r.status,
      reply: staticReplies[r.id],
    })),
    ...userReviews.map((r) => ({ ...r, isStatic: false as const })),
  ];

  const updateStatus = (review: AnyReview, status: ReviewStatus) => {
    if (review.isStatic) {
      setStaticStatuses((p) => ({ ...p, [review.id]: status }));
    } else {
      reviewStore.updateStatus(review.id, status);
      refreshUserReviews();
    }
    showToast(status === "approved" ? "리뷰가 게시됐어요!" : status === "rejected" ? "리뷰를 숨김 처리했어요." : "검토 대기로 변경됐어요.");
  };

  const submitReply = (review: AnyReview) => {
    if (!replyText.trim()) return;
    if (review.isStatic) {
      setStaticReplies((p) => ({ ...p, [review.id]: replyText.trim() }));
    } else {
      reviewStore.updateReply(review.id, replyText.trim());
      refreshUserReviews();
    }
    setReplyTarget(null);
    setReplyText("");
    showToast("답글이 등록됐어요!");
  };

  const deleteReview = (review: AnyReview) => {
    if (review.isStatic) {
      showToast("기본 리뷰는 삭제할 수 없어요.");
      return;
    }
    reviewStore.remove(review.id);
    refreshUserReviews();
    showToast("리뷰가 삭제됐어요.");
  };

  const filtered = filter === "all" ? allReviews : allReviews.filter((r) => {
    const status = r.isStatic ? (staticStatuses[r.id] ?? r.status) : r.status;
    return status === filter;
  });

  const counts = {
    all: allReviews.length,
    pending: allReviews.filter((r) => (r.isStatic ? staticStatuses[r.id] ?? r.status : r.status) === "pending").length,
    approved: allReviews.filter((r) => (r.isStatic ? staticStatuses[r.id] ?? r.status : r.status) === "approved").length,
    rejected: allReviews.filter((r) => (r.isStatic ? staticStatuses[r.id] ?? r.status : r.status) === "rejected").length,
  };

  const newCount = userReviews.filter((r) => r.status === "pending").length;

  return (
    <div className="max-w-4xl">
      {toast && (
        <div className="fixed top-6 right-6 z-50 bg-stone-800 text-white text-sm px-4 py-3 rounded-xl flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-check-line"></i>
          </div>
          {toast}
        </div>
      )}

      <div className="mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-stone-800 flex items-center gap-2">
            리뷰 관리
            {newCount > 0 && (
              <span className="inline-flex items-center justify-center px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full min-w-5">
                {newCount}
              </span>
            )}
          </h2>
          <p className="text-sm text-stone-400 mt-1">고객 리뷰를 검토하고 게시 여부를 결정하세요.</p>
        </div>
      </div>

      {/* 신규 리뷰 알림 배너 */}
      {newCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 flex-shrink-0">
            <i className="ri-notification-3-line text-amber-600 text-sm"></i>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800">새 리뷰 {newCount}개가 검토를 기다리고 있어요!</p>
            <p className="text-xs text-amber-600 mt-0.5">승인하면 숙소 상세 페이지에 바로 노출됩니다.</p>
          </div>
          <button
            onClick={() => setFilter("pending")}
            className="px-3 py-1.5 bg-amber-500 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap hover:bg-amber-600"
          >
            바로 확인
          </button>
        </div>
      )}

      {/* 필터 탭 */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {(["all", "pending", "approved", "rejected"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all ${
              filter === f ? "bg-stone-800 text-white" : "bg-white text-stone-500 border border-stone-200 hover:bg-stone-50"
            }`}
          >
            {f === "all" && `전체 ${counts.all}`}
            {f === "pending" && (
              <span className="flex items-center gap-1.5">
                검토 대기
                {counts.pending > 0 && (
                  <span className="inline-flex items-center justify-center w-4 h-4 bg-amber-500 text-white text-xs font-bold rounded-full">
                    {counts.pending}
                  </span>
                )}
              </span>
            )}
            {f === "approved" && `게시 중 ${counts.approved}`}
            {f === "rejected" && `숨김 ${counts.rejected}`}
          </button>
        ))}
      </div>

      {/* 리뷰 목록 */}
      <div className="space-y-4">
        {filtered.length === 0 && (
          <div className="text-center py-16 text-stone-400 bg-white rounded-xl border border-stone-100">
            해당 상태의 리뷰가 없어요.
          </div>
        )}
        {filtered.map((review) => {
          const currentStatus = review.isStatic ? (staticStatuses[review.id] ?? review.status) : review.status;
          const currentReply = review.isStatic ? staticReplies[review.id] : review.reply;
          const name = review.isStatic ? review.name : review.nickname;
          const location = review.isStatic ? review.location : "직접 작성";
          const isNew = !review.isStatic && currentStatus === "pending";

          return (
            <div key={review.id} className={`bg-white rounded-xl border p-5 ${isNew ? "border-amber-300 ring-1 ring-amber-200" : "border-stone-100"}`}>
              {isNew && (
                <div className="flex items-center gap-1.5 mb-3">
                  <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-semibold rounded-full">NEW</span>
                  <span className="text-xs text-amber-600">사용자가 직접 작성한 신규 리뷰예요.</span>
                </div>
              )}
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-stone-800 text-sm">{name}</span>
                    <span className="text-xs text-stone-400">{location}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusLabel[currentStatus].className}`}>
                      {statusLabel[currentStatus].label}
                    </span>
                    {review.isStatic && (
                      <span className="px-2 py-0.5 bg-stone-100 text-stone-400 text-xs rounded-full">기본</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="w-3 h-3 flex items-center justify-center">
                        <i className={`${i < review.rating ? "ri-star-fill" : "ri-star-line"} text-amber-400 text-xs`}></i>
                      </div>
                    ))}
                    <span className="text-xs text-stone-400 ml-1">{review.date}</span>
                    <span className="text-xs text-stone-300 mx-1">·</span>
                    <span className="text-xs text-stone-400">{review.accommodation}</span>
                  </div>
                  <p className="text-sm text-stone-600 mt-2 leading-relaxed">{review.text}</p>

                  {currentReply && (
                    <div className="mt-3 bg-stone-50 rounded-lg px-4 py-3 border-l-2 border-stone-300">
                      <p className="text-xs font-medium text-stone-600 mb-1">호스트 답글</p>
                      <p className="text-sm text-stone-600">{currentReply}</p>
                    </div>
                  )}

                  {replyTarget === review.id && (
                    <div className="mt-3">
                      <textarea
                        className="w-full border border-stone-200 rounded-lg p-3 text-sm resize-none focus:outline-none focus:border-stone-400 text-stone-700"
                        rows={3}
                        placeholder="답글을 입력하세요..."
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        maxLength={500}
                        autoFocus
                      />
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => submitReply(review)}
                          className="px-4 py-1.5 bg-stone-800 text-white text-xs rounded-md cursor-pointer whitespace-nowrap hover:bg-stone-700"
                        >
                          등록
                        </button>
                        <button
                          onClick={() => { setReplyTarget(null); setReplyText(""); }}
                          className="px-4 py-1.5 border border-stone-200 text-stone-500 text-xs rounded-md cursor-pointer whitespace-nowrap hover:bg-stone-50"
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* 액션 버튼 */}
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  {currentStatus === "pending" && (
                    <>
                      <button
                        onClick={() => updateStatus(review, "approved")}
                        className="px-3 py-1.5 bg-emerald-500 text-white text-xs rounded-md cursor-pointer whitespace-nowrap hover:bg-emerald-600"
                      >
                        <i className="ri-check-line mr-1"></i>승인
                      </button>
                      <button
                        onClick={() => updateStatus(review, "rejected")}
                        className="px-3 py-1.5 bg-stone-200 text-stone-600 text-xs rounded-md cursor-pointer whitespace-nowrap hover:bg-stone-300"
                      >
                        <i className="ri-eye-off-line mr-1"></i>거절
                      </button>
                    </>
                  )}
                  {currentStatus === "approved" && (
                    <button
                      onClick={() => updateStatus(review, "rejected")}
                      className="px-3 py-1.5 bg-stone-200 text-stone-600 text-xs rounded-md cursor-pointer whitespace-nowrap hover:bg-stone-300"
                    >
                      <i className="ri-eye-off-line mr-1"></i>숨김
                    </button>
                  )}
                  {currentStatus === "rejected" && (
                    <button
                      onClick={() => updateStatus(review, "approved")}
                      className="px-3 py-1.5 bg-emerald-500 text-white text-xs rounded-md cursor-pointer whitespace-nowrap hover:bg-emerald-600"
                    >
                      <i className="ri-eye-line mr-1"></i>게시
                    </button>
                  )}
                  <button
                    onClick={() => { setReplyTarget(review.id); setReplyText(currentReply ?? ""); }}
                    className="px-3 py-1.5 border border-stone-200 text-stone-500 text-xs rounded-md cursor-pointer whitespace-nowrap hover:bg-stone-50"
                  >
                    <i className="ri-reply-line mr-1"></i>답글
                  </button>
                  {!review.isStatic && (
                    <button
                      onClick={() => deleteReview(review)}
                      className="px-3 py-1.5 border border-red-100 text-red-400 text-xs rounded-md cursor-pointer whitespace-nowrap hover:bg-red-50"
                    >
                      <i className="ri-delete-bin-line mr-1"></i>삭제
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
